import { useEffect, useLayoutEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useDispatch, useStore } from 'react-redux'
import {
  clearContext,
  mergeContext,
} from '../_store/reducers/contextReducer'
import { setUserInformation } from '../_store/reducers/userReducer'
import {
  getBrandList,
  getOneSimulationDetails,
  getProjectDetails,
  getProjectsList,
  getSimulationsList,
  getUserInfo,
  setAgency,
} from '../requests'
import useRole from './getRole'
import {
  parseAppQuery,
  parseSimulationDetailPath,
  toNumericId,
} from '../utils/urlContextParams'

function simListFromResponse(res) {
  const d = res?.data
  return d?.aiSimulations ?? d?.ai_simulations ?? []
}

function projectsListFromResponse(res) {
  const d = res?.data
  return Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : []
}

/**
 * Reads agency_id, brand_id, project_id, simulation_id (query + simulation detail path)
 * into Redux context so deep links match header, nav, and list pages.
 */
export function useContextFromUrl() {
  const location = useLocation()
  const dispatch = useDispatch()
  const store = useStore()
  const role = (useRole() || 'user').toLowerCase()
  const runId = useRef(0)

  useLayoutEffect(() => {
    const q = parseAppQuery(location.search)
    const pathInfo = parseSimulationDetailPath(location.pathname)
    const patch = {}
    const bid = q.brand_id != null && q.brand_id !== '' ? toNumericId(q.brand_id) : null
    const pid = q.project_id != null && q.project_id !== '' ? toNumericId(q.project_id) : null
    let sid =
      q.simulation_id != null && q.simulation_id !== ''
        ? toNumericId(q.simulation_id)
        : null
    if (pathInfo.simulationId) {
      sid = toNumericId(pathInfo.simulationId)
    }
    if (bid != null) patch.brand_id = bid
    if (pid != null) patch.project_id = pid
    if (sid != null) patch.simulation_id = sid
    if (q.brand_name) patch.brand_name = String(q.brand_name)
    if (q.project_name) patch.project_name = String(q.project_name)
    if (q.simulation_name) patch.simulation_name = String(q.simulation_name)
    if (Object.keys(patch).length) dispatch(mergeContext(patch))
  }, [location.pathname, location.search, dispatch])

  useEffect(() => {
    const myRun = ++runId.current
    const q = parseAppQuery(location.search)
    const pathInfo = parseSimulationDetailPath(location.pathname)
    const urlAgencyId =
      q.agency_id != null && q.agency_id !== '' ? toNumericId(q.agency_id) : null

    let brandId = q.brand_id != null && q.brand_id !== '' ? toNumericId(q.brand_id) : null
    let projectId = q.project_id != null && q.project_id !== '' ? toNumericId(q.project_id) : null
    let simulationId =
      q.simulation_id != null && q.simulation_id !== '' ? toNumericId(q.simulation_id) : null
    if (pathInfo.simulationId) {
      simulationId = toNumericId(pathInfo.simulationId)
    }

    const hasScope =
      urlAgencyId != null ||
      brandId != null ||
      projectId != null ||
      simulationId != null
    if (!hasScope) return

    const go = async () => {
      const user = store.getState().user

      if (
        role === 'super_admin' &&
        urlAgencyId != null &&
        String(urlAgencyId) !== String(user?.agencyId ?? '')
      ) {
        dispatch(clearContext())
        const reapply = {}
        if (brandId != null) reapply.brand_id = brandId
        if (projectId != null) reapply.project_id = projectId
        if (simulationId != null) reapply.simulation_id = simulationId
        if (q.brand_name) reapply.brand_name = String(q.brand_name)
        if (q.project_name) reapply.project_name = String(q.project_name)
        if (q.simulation_name) reapply.simulation_name = String(q.simulation_name)
        if (Object.keys(reapply).length) dispatch(mergeContext(reapply))
        try {
          const res = await setAgency(Number(urlAgencyId))
          if (myRun !== runId.current) return
          if (res?.status < 400) {
            const info = await getUserInfo()
            if (myRun !== runId.current) return
            const agency = info?.data?.agency
            if (agency?.id != null) {
              dispatch(
                setUserInformation(
                  user.fullName,
                  user.userId,
                  user.email,
                  user.status,
                  user.isSuperAdmin,
                  agency.id,
                  user.invitedById,
                  agency.name ?? '',
                  agency.email ?? user.agency_email ?? '',
                  user.role
                )
              )
            }
          }
        } catch (e) {
          console.error('Failed to sync agency from URL', e)
        }
        if (myRun !== runId.current) return
      }

      let nextBrandId = brandId
      let nextProjectId = projectId
      let nextSimulationId = simulationId
      const fromApi = {}

      try {
        if (nextSimulationId != null && (nextBrandId == null || nextProjectId == null)) {
          const res = await getOneSimulationDetails(nextSimulationId)
          if (myRun !== runId.current) return
          if (res?.status < 400) {
            const data = res?.data ?? res
            const p = data?.project ?? {}
            const b = p.brand ?? {}
            if (nextBrandId == null && b.id != null) {
              nextBrandId = toNumericId(b.id)
              fromApi.brand_id = nextBrandId
              fromApi.brand_name = b.name ?? ''
            }
            if (nextProjectId == null && p.id != null) {
              nextProjectId = toNumericId(p.id)
              fromApi.project_id = nextProjectId
              fromApi.project_name = p.name ?? ''
            }
            fromApi.simulation_id = nextSimulationId
          }
        } else if (nextProjectId != null && nextBrandId == null) {
          const res = await getProjectDetails({ id: nextProjectId })
          if (myRun !== runId.current) return
          if (res?.status < 400) {
            const proj = res?.data ?? res
            const b = proj?.brand ?? {}
            if (b.id != null) {
              nextBrandId = toNumericId(b.id)
              fromApi.brand_id = nextBrandId
              fromApi.brand_name = b.name ?? ''
              fromApi.project_id = nextProjectId
              fromApi.project_name = proj?.name ?? ''
            }
          }
        }

        if (Object.keys(fromApi).length) {
          dispatch(mergeContext(fromApi))
        }
      } catch (e) {
        console.error('Failed to resolve scope from URL', e)
      }

      if (myRun !== runId.current) return

      const ctx = store.getState().context
      const enrich = {}

      try {
        const effectiveBrand = nextBrandId ?? ctx.brand_id
        const effectiveProject = nextProjectId ?? ctx.project_id
        const effectiveSim = nextSimulationId ?? ctx.simulation_id

        if (effectiveBrand != null && !(ctx.brand_name || '').trim()) {
          const brandsRes = await getBrandList()
          if (myRun !== runId.current) return
          const list = Array.isArray(brandsRes?.data) ? brandsRes.data : []
          const b = list.find((x) => String(x.id) === String(effectiveBrand))
          if (b?.name) enrich.brand_name = b.name
        }
        if (
          effectiveProject != null &&
          effectiveBrand != null &&
          !(ctx.project_name || '').trim()
        ) {
          const pr = await getProjectsList({ brand_id: effectiveBrand })
          if (myRun !== runId.current) return
          const plist = projectsListFromResponse(pr)
          const p = plist.find((x) => String(x.id) === String(effectiveProject))
          if (p?.name) enrich.project_name = p.name
        }
        if (
          effectiveSim != null &&
          effectiveProject != null &&
          !(ctx.simulation_name || '').trim()
        ) {
          const sr = await getSimulationsList({ project_id: effectiveProject })
          if (myRun !== runId.current) return
          const slist = simListFromResponse(sr)
          const s = slist.find((x) => String(x.id) === String(effectiveSim))
          if (s?.name) enrich.simulation_name = s.name
        }
      } catch (e) {
        console.error('Failed to enrich context labels from URL', e)
      }

      if (myRun !== runId.current) return
      if (Object.keys(enrich).length) dispatch(mergeContext(enrich))
    }

    go()
  }, [location.pathname, location.search, dispatch, role, store])
}
